import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { PieChart } from 'react-minimal-pie-chart';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip } from 'recharts';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getGeneralStats, getMonthlyStats } from '../services/statisticsService';
import { formatDate } from '../utils/dateUtils';
import { STATUS_COLORS, PRIORITY_COLORS } from '../utils/statusColors';
import { useAuth } from '../contexts/AuthContext';
import ServiceList from '../components/services/ServiceList';
import AllRequestsTab from '../components/admin/AllRequestsTab';
import InterventionsTab from '../components/admin/InterventionsTab';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Composant TabPanel pour gérer le contenu des onglets
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminDashboard = () => {
  const { currentUser, userSchool } = useAuth();
  const [stats, setStats] = useState({
    totalRequests: 0,
    byStatus: {},
    byPriority: {},
    monthlyData: []
  });
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    let isMounted = true;
    
    const fetchStats = async () => {
      if (!currentUser) {
        console.log('AdminDashboard: Pas d\'utilisateur connecté');
        return;
      }

      try {
        console.log('AdminDashboard: Début de la récupération des statistiques');
        setError(null);
        setLoading(true);
        
        const [generalStats, monthlyStats] = await Promise.all([
          getGeneralStats(currentUser, userSchool),
          getMonthlyStats(currentUser, userSchool)
        ]);
        
        console.log('AdminDashboard: Statistiques récupérées', {
          generalStats,
          monthlyStats
        });
        
        if (isMounted) {
          const newStats = {
            totalRequests: generalStats.totalRequests || 0,
            byStatus: generalStats.byStatus || {},
            byPriority: generalStats.byPriority || {},
            monthlyData: monthlyStats.monthlyData || []
          };
          
          console.log('AdminDashboard: Mise à jour des stats', newStats);
          console.log('AdminDashboard: Demandes récentes', monthlyStats.recentRequests);
          
          setStats(newStats);
          setRecentRequests(monthlyStats.recentRequests || []);
        }
      } catch (error) {
        console.error('AdminDashboard: Erreur lors de la récupération des statistiques:', error);
        if (isMounted) {
          setError('Erreur lors du chargement des statistiques');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStats();
    return () => {
      isMounted = false;
    };
  }, [currentUser, userSchool]);

  // Configuration du graphique des demandes mensuelles
  const monthlyChartData = {
    labels: stats.monthlyData?.map(data => data.month) || [],
    datasets: [
      {
        label: 'Total des demandes',
        data: stats.monthlyData?.map(data => data.total) || [],
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 1
      }
    ]
  };

  const monthlyChartOptions = {
    responsive: true,
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: 'Évolution des demandes par mois',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        padding: 12,
        cornerRadius: 4,
        displayColors: true,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            const dataIndex = context.dataIndex;
            const monthData = stats.monthlyData?.[dataIndex] || {};
            const total = monthData.total || 0;
            const pending = monthData.byStatus?.pending || 0;
            const inProgress = monthData.byStatus?.inProgress || 0;
            const completed = monthData.byStatus?.completed || 0;
            
            return [
              `📊 Total: ${total} demande(s)`,
              '',
              `⏳ En attente: ${pending} (${total > 0 ? Math.round((pending/total)*100) : 0}%)`,
              `🔄 En cours: ${inProgress} (${total > 0 ? Math.round((inProgress/total)*100) : 0}%)`,
              `✅ Terminées: ${completed} (${total > 0 ? Math.round((completed/total)*100) : 0}%)`
            ];
          },
          title: function(context) {
            return `📅 ${context[0].label}`;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          precision: 0
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Veuillez rafraîchir la page ou réessayer plus tard
        </Typography>
      </Box>
    );
  }

  // Préparation des données pour les graphiques
  const statusData = [
    { title: 'Terminées', value: stats.byStatus.completed || 0, color: STATUS_COLORS.completed },
    { title: 'En cours', value: stats.byStatus.inProgress || 0, color: STATUS_COLORS.inProgress },
    { title: 'En attente', value: stats.byStatus.pending || 0, color: STATUS_COLORS.pending },
  ].filter(item => item.value > 0);

  const priorityData = [
    { title: 'Urgent', value: stats.byPriority.urgent || 0, color: PRIORITY_COLORS.urgent },
    { title: 'Élevé', value: stats.byPriority.high || 0, color: PRIORITY_COLORS.high },
    { title: 'Moyen', value: stats.byPriority.medium || 0, color: PRIORITY_COLORS.medium },
    { title: 'Faible', value: stats.byPriority.low || 0, color: PRIORITY_COLORS.low },
  ].filter(item => item.value > 0);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin dashboard tabs">
          <Tab label="Vue d'ensemble" />
          <Tab label="Demandes" />
          <Tab label="Interventions" />
          <Tab label="Gestion des services" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ flexGrow: 1, p: 3 }}>
          {/* KPIs principaux */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total des demandes
                  </Typography>
                  <Typography variant="h3" component="div" color="primary">
                    {stats.totalRequests}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Toutes périodes confondues
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    En attente
                  </Typography>
                  <Typography variant="h3" component="div" color="warning.main">
                    {stats.byStatus.pending || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Demandes non traitées
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    En cours
                  </Typography>
                  <Typography variant="h3" component="div" color="info.main">
                    {stats.byStatus.inProgress || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Demandes en traitement
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Terminées
                  </Typography>
                  <Typography variant="h3" component="div" color="success.main">
                    {stats.byStatus.completed || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Demandes résolues
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={4}>
            {/* Graphique des statuts */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Évolution mensuelle
                </Typography>
                <Box sx={{ height: 300 }}>
                  <Bar
                    data={monthlyChartData}
                    options={monthlyChartOptions}
                  />
                </Box>
                {stats.monthlyData && stats.monthlyData.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Tendance des demandes
                    </Typography>
                    <ResponsiveContainer width="100%" height={100}>
                      <LineChart
                        data={stats.monthlyData.map(data => ({
                          month: data.month,
                          total: data.total || 0
                        }))}
                        margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis hide />
                        <RechartsTooltip
                          formatter={(value) => [`${value} demandes`, 'Total']}
                          labelFormatter={(label) => `Mois: ${label}`}
                          contentStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', color: '#fff', borderRadius: '4px', border: 'none' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="total"
                          stroke="#8884d8"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5, strokeWidth: 0 }}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-out"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Graphique des statuts */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Répartition des demandes
                </Typography>
                
                {/* Demandes par statut */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Demandes par statut
                    <Chip label={`${stats.totalRequests} total`} size="small" />
                  </Typography>
                  <Box sx={{ height: 200, position: 'relative' }}>
                    <PieChart
                      data={[
                        { title: 'En attente', value: stats.byStatus.pending || 0, color: STATUS_COLORS.pending },
                        { title: 'En cours', value: stats.byStatus.inProgress || 0, color: STATUS_COLORS.inProgress },
                        { title: 'Terminées', value: stats.byStatus.completed || 0, color: STATUS_COLORS.completed }
                      ]}
                      lineWidth={40}
                      paddingAngle={5}
                      rounded
                      animate
                      animationDuration={800}
                      animationEasing="ease-out"
                      label={({ dataEntry }) => dataEntry.value > 0 ? dataEntry.value : ''}
                      labelStyle={{
                        fontSize: '6px',
                        fontFamily: 'sans-serif',
                        fill: '#fff',
                        fontWeight: 'bold'
                      }}
                      labelPosition={70}
                      data-tooltip-id="status-chart"
                      data-tooltip-html={({ dataEntry }) => {
                        const percentage = Math.round((dataEntry.value / (stats.totalRequests || 1)) * 100);
                        const remaining = stats.totalRequests - dataEntry.value;
                        return `<div style="padding: 16px; background-color: rgba(0,0,0,0.9); color: white; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.3); min-width: 200px;">
                          <div style="font-size: 16px; font-weight: bold; margin-bottom: 10px; color: ${dataEntry.color}; border-bottom: 2px solid ${dataEntry.color}; padding-bottom: 8px;">
                            📊 ${dataEntry.title}
                          </div>
                          <div style="font-size: 14px; margin-bottom: 6px; display: flex; justify-content: space-between;">
                            <span>Nombre:</span>
                            <span style="font-weight: bold; color: ${dataEntry.color}">${dataEntry.value} demande(s)</span>
                          </div>
                          <div style="font-size: 14px; margin-bottom: 6px; display: flex; justify-content: space-between;">
                            <span>Pourcentage:</span>
                            <span style="font-weight: bold; color: ${dataEntry.color}">${percentage}%</span>
                          </div>
                          <div style="font-size: 14px; display: flex; justify-content: space-between;">
                            <span>Autres:</span>
                            <span style="font-weight: bold;">${remaining} demande(s)</span>
                          </div>
                          <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.2); font-size: 12px; color: rgba(255,255,255,0.7);">
                            Total: ${stats.totalRequests} demande(s)
                          </div>
                        </div>`;
                      }}
                      segmentsStyle={{ transition: 'stroke-width 0.3s, opacity 0.3s' }}
                      segmentsShift={(index) => (index === 0 ? 1 : 0.5)}
                      onMouseOver={(_, index) => {
                        return [
                          { strokeWidth: 45, opacity: 0.9 },
                          { strokeWidth: 45, opacity: 0.9 },
                          { strokeWidth: 45, opacity: 0.9 }
                        ][index];
                      }}
                      onMouseOut={() => {
                        return { strokeWidth: 40, opacity: 1 };
                      }}
                    />
                    <ReactTooltip id="status-chart" place="top" effect="solid" />
                  </Box>
                  
                  {/* Légende détaillée pour les statuts */}
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: STATUS_COLORS.pending, borderRadius: '50%' }} />
                        <Typography variant="body2">En attente</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {stats.byStatus.pending || 0} ({Math.round(((stats.byStatus.pending || 0) / (stats.totalRequests || 1)) * 100)}%)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: STATUS_COLORS.inProgress, borderRadius: '50%' }} />
                        <Typography variant="body2">En cours</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {stats.byStatus.inProgress || 0} ({Math.round(((stats.byStatus.inProgress || 0) / (stats.totalRequests || 1)) * 100)}%)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: STATUS_COLORS.completed, borderRadius: '50%' }} />
                        <Typography variant="body2">Terminées</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {stats.byStatus.completed || 0} ({Math.round(((stats.byStatus.completed || 0) / (stats.totalRequests || 1)) * 100)}%)
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                {/* Demandes par priorité */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Demandes par priorité
                  </Typography>
                  <Box sx={{ height: 200, position: 'relative' }}>
                    <PieChart
                      data={[
                        { title: 'Urgente', value: stats.byPriority.urgent || 0, color: PRIORITY_COLORS.urgent },
                        { title: 'Haute', value: stats.byPriority.high || 0, color: PRIORITY_COLORS.high },
                        { title: 'Moyenne', value: stats.byPriority.medium || 0, color: PRIORITY_COLORS.medium },
                        { title: 'Basse', value: stats.byPriority.low || 0, color: PRIORITY_COLORS.low }
                      ]}
                      lineWidth={40}
                      paddingAngle={5}
                      rounded
                      animate
                      animationDuration={800}
                      animationEasing="ease-out"
                    label={({ dataEntry }) => dataEntry.value > 0 ? dataEntry.value : ''}
                    labelStyle={{
                      fontSize: '6px',
                      fontFamily: 'sans-serif',
                      fill: '#fff',
                      fontWeight: 'bold'
                    }}
                    labelPosition={70}
                    data-tooltip-id="priority-chart"
                    data-tooltip-html={({ dataEntry }) => {
                      const percentage = Math.round((dataEntry.value / (stats.totalRequests || 1)) * 100);
                      const remaining = stats.totalRequests - dataEntry.value;
                      const priorityIcons = {
                        'Urgente': '🔴',
                        'Haute': '🟠',
                        'Moyenne': '🟡',
                        'Basse': '🟢'
                      };
                      return `<div style="padding: 16px; background-color: rgba(0,0,0,0.9); color: white; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.3); min-width: 200px;">
                        <div style="font-size: 16px; font-weight: bold; margin-bottom: 10px; color: ${dataEntry.color}; border-bottom: 2px solid ${dataEntry.color}; padding-bottom: 8px;">
                          ${priorityIcons[dataEntry.title] || '⚡'} Priorité ${dataEntry.title}
                        </div>
                        <div style="font-size: 14px; margin-bottom: 6px; display: flex; justify-content: space-between;">
                          <span>Nombre:</span>
                          <span style="font-weight: bold; color: ${dataEntry.color}">${dataEntry.value} demande(s)</span>
                        </div>
                        <div style="font-size: 14px; margin-bottom: 6px; display: flex; justify-content: space-between;">
                          <span>Pourcentage:</span>
                          <span style="font-weight: bold; color: ${dataEntry.color}">${percentage}%</span>
                        </div>
                        <div style="font-size: 14px; display: flex; justify-content: space-between;">
                          <span>Autres priorités:</span>
                          <span style="font-weight: bold;">${remaining} demande(s)</span>
                        </div>
                        <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.2); font-size: 12px; color: rgba(255,255,255,0.7);">
                          Total: ${stats.totalRequests} demande(s)
                        </div>
                      </div>`;
                    }}
                    segmentsStyle={{ transition: 'stroke-width 0.3s, opacity 0.3s' }}
                    segmentsShift={(index) => (index === 0 ? 1 : 0.5)}
                    onMouseOver={(_, index) => {
                      return [
                        { strokeWidth: 45, opacity: 0.9 },
                        { strokeWidth: 45, opacity: 0.9 },
                        { strokeWidth: 45, opacity: 0.9 },
                        { strokeWidth: 45, opacity: 0.9 }
                      ][index];
                    }}
                    onMouseOut={() => {
                      return { strokeWidth: 40, opacity: 1 };
                    }}
                  />
                  <ReactTooltip id="priority-chart" place="top" effect="solid" />
                </Box>
                
                {/* Légende détaillée pour les priorités */}
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: PRIORITY_COLORS.urgent, borderRadius: '50%' }} />
                      <Typography variant="body2">Urgente</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {stats.byPriority.urgent || 0} ({Math.round(((stats.byPriority.urgent || 0) / (stats.totalRequests || 1)) * 100)}%)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: PRIORITY_COLORS.high, borderRadius: '50%' }} />
                      <Typography variant="body2">Haute</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {stats.byPriority.high || 0} ({Math.round(((stats.byPriority.high || 0) / (stats.totalRequests || 1)) * 100)}%)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: PRIORITY_COLORS.medium, borderRadius: '50%' }} />
                      <Typography variant="body2">Moyenne</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {stats.byPriority.medium || 0} ({Math.round(((stats.byPriority.medium || 0) / (stats.totalRequests || 1)) * 100)}%)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: PRIORITY_COLORS.low, borderRadius: '50%' }} />
                      <Typography variant="body2">Basse</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {stats.byPriority.low || 0} ({Math.round(((stats.byPriority.low || 0) / (stats.totalRequests || 1)) * 100)}%)
                    </Typography>
                  </Box>
                </Box>
              </Box>
              </Paper>
            </Grid>

          {/* Graphique des demandes mensuelles */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Évolution des demandes
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ height: 300 }}>
                    <Bar data={monthlyChartData} options={monthlyChartOptions} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Nouveau graphique en barres */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Vue d'ensemble des demandes
              </Typography>
              <Box sx={{ height: 300, position: 'relative' }}>
                <Bar
                  data={{
                    labels: ['Statuts', 'Priorités'],
                    datasets: [
                      {
                        label: 'Terminées',
                        data: [stats.byStatus.completed || 0, null],
                        backgroundColor: STATUS_COLORS.completed,
                      },
                      {
                        label: 'En cours',
                        data: [stats.byStatus.inProgress || 0, null],
                        backgroundColor: STATUS_COLORS.inProgress,
                      },
                      {
                        label: 'En attente',
                        data: [stats.byStatus.pending || 0, null],
                        backgroundColor: STATUS_COLORS.pending,
                      },
                      {
                        label: 'Urgent',
                        data: [null, stats.byPriority.urgent || 0],
                        backgroundColor: PRIORITY_COLORS.urgent,
                      },
                      {
                        label: 'Élevé',
                        data: [null, stats.byPriority.high || 0],
                        backgroundColor: PRIORITY_COLORS.high,
                      },
                      {
                        label: 'Moyen',
                        data: [null, stats.byPriority.medium || 0],
                        backgroundColor: PRIORITY_COLORS.medium,
                      },
                      {
                        label: 'Faible',
                        data: [null, stats.byPriority.low || 0],
                        backgroundColor: PRIORITY_COLORS.low,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        stacked: true,
                      },
                      y: {
                        stacked: true,
                        beginAtZero: true,
                      },
                    },
                    plugins: {
                      legend: {
                        position: 'bottom',
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.raw || 0;
                            const datasetLabel = context.dataset.label;
                            const categoryIndex = context.dataIndex;
                            const category = ['Statuts', 'Priorités'][categoryIndex];
                            
                            // Calculer le pourcentage
                            let total = 0;
                            if (category === 'Statuts') {
                              total = (stats.byStatus.completed || 0) + 
                                      (stats.byStatus.inProgress || 0) + 
                                      (stats.byStatus.pending || 0);
                            } else {
                              total = (stats.byPriority.urgent || 0) + 
                                      (stats.byPriority.high || 0) + 
                                      (stats.byPriority.medium || 0) + 
                                      (stats.byPriority.low || 0);
                            }
                              
                              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                              
                              return value > 0 ? `${datasetLabel}: ${value} (${percentage}%)` : null;
                            },
                            title: function(context) {
                              return context[0].label;
                            }
                          },
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          padding: 12,
                          bodySpacing: 6,
                          usePointStyle: true
                        }
                      },
                      interaction: {
                        intersect: false,
                        mode: 'index'
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Top demandeurs et services techniques */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top 5 - Services demandeurs
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Services ayant créé le plus de demandes
                </Typography>
                <List>
                  {(() => {
                    // Compter les demandes par service demandeur
                    const requestsByRequester = {};
                    recentRequests.forEach(req => {
                      const requester = req.createdByName || 'Non spécifié';
                      requestsByRequester[requester] = (requestsByRequester[requester] || 0) + 1;
                    });
                    
                    // Trier et prendre le top 5
                    const topRequesters = Object.entries(requestsByRequester)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5);
                    
                    return topRequesters.length > 0 ? (
                      topRequesters.map(([name, count], index) => (
                        <React.Fragment key={name}>
                          {index > 0 && <Divider />}
                          <ListItem>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip label={`#${index + 1}`} size="small" color="primary" />
                                  <Typography variant="body1">{name}</Typography>
                                </Box>
                              }
                              secondary={`${count} demande(s)`}
                            />
                          </ListItem>
                        </React.Fragment>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        Aucune donnée disponible
                      </Typography>
                    );
                  })()}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top 5 - Services techniques
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Services techniques les plus sollicités
                </Typography>
                <List>
                  {(() => {
                    // Compter les demandes par service technique
                    const requestsByService = {};
                    recentRequests.forEach(req => {
                      const service = req.assignedServiceName || 'Non assigné';
                      requestsByService[service] = (requestsByService[service] || 0) + 1;
                    });
                    
                    // Trier et prendre le top 5
                    const topServices = Object.entries(requestsByService)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5);
                    
                    return topServices.length > 0 ? (
                      topServices.map(([name, count], index) => (
                        <React.Fragment key={name}>
                          {index > 0 && <Divider />}
                          <ListItem>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip label={`#${index + 1}`} size="small" color="secondary" />
                                  <Typography variant="body1">{name}</Typography>
                                </Box>
                              }
                              secondary={`${count} demande(s) assignée(s)`}
                            />
                          </ListItem>
                        </React.Fragment>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        Aucune donnée disponible
                      </Typography>
                    );
                  })()}
                </List>
              </Paper>
            </Grid>

            {/* Liste des demandes récentes */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Demandes récentes
                </Typography>
                <List>
                  {recentRequests.length > 0 ? (
                    recentRequests.map((request, index) => (
                      <React.Fragment key={request.id}>
                        {index > 0 && <Divider />}
                        <ListItem>
                          <ListItemText
                            primary={request.title || 'Sans titre'}
                            secondary={
                              <Box component="span">
                                <Typography component="span" variant="body2" color="text.secondary">
                                  Créée le {formatDate(request.createdAt)}
                                </Typography>
                                {request.createdByName && (
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    {' • Par: '}{request.createdByName}
                                  </Typography>
                                )}
                                {request.assignedServiceName && (
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    {' • Service: '}{request.assignedServiceName}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      Aucune demande récente
                    </Typography>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom>
            Gestion des demandes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vue complète de toutes les demandes de travaux avec filtres avancés
          </Typography>
          <AllRequestsTab />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom>
            Suivi des interventions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Calendrier et planning de toutes les interventions des services techniques
          </Typography>
          <InterventionsTab />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom>
            Gestion des services
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Administration des services de l'établissement
          </Typography>
          <ServiceList />
        </Box>
      </TabPanel>
    </Box>
  );
};

export default AdminDashboard;
